import {
  AllowedBookingDTO,
  AllowedBookingRequestDTO,
  BookingDTO,
  CreateBookingDTO,
  CreateBookingResultDTO,
  GetHistoryDTO,
  PaginatedBookingsDTO,
  PartialUpdateBookingDTO,
} from '@forto-dev/booking-v2-api'
import { SubscriptionResponse } from '@forto-dev/subscriptions-models'
import {
  CreateProfileRequest,
  GetProfilesResponse,
  ProfileResponse,
  SearchFeatureFlagsRequest,
  SearchFeatureFlagsResponse,
} from '@freighthub/user-profile-core-client'
import { IActivityFeedJSON, IFreightieJSON, TIdType } from 'features/ActivityFeed'
import { GenericPaymentTerm, Invoice, InvoiceClaim, InvoiceClaimResponse, InvoiceClaimsRequest } from 'features/Billing'
import { BookingHistoryType, BookingsParams } from 'features/Booking'
import {
  BookQuoteSheetResponse,
  BuyersConsolidationQuoteServicesJSON,
  CargoType,
  CustomerPreference,
  getURLSearchString,
  IQuoteSheet,
  OceanCarrier,
} from 'features/BookingFlow'
import { ConversionRatesRequest, ConversionRatesResponse } from 'features/CurrencyConversion'
import {
  ContactV2DTO,
  IBillingEntityJSON,
  IContactJSON,
  IContactResJSON,
  ILegalEntityJSON,
  ILegalEntityResJSON,
  ILocationModelJSON,
  ILocationResJSON,
  IOrganizationJSON,
  IOrganizationResJSON,
  IVATResponseJSON,
  LegalEntityV3DTO,
} from 'features/Directory'
import { DocumentType, ManualQuoteDocument, PaginatedDocuments } from 'features/Documents'
import {
  GeneratedToken,
  PaginatedSubscriptions,
  PaginatedTokenLogs,
  PaginatedTokens,
  SubscriptionDTO,
} from 'features/Integrations'
import { IMQAdditionalRequirements } from 'features/ManualQuotes'
import { CompanyDocumentTemplate } from 'features/Settings'
import {
  CostBreakdown,
  CustomStatusHistoryType,
  CustomStatusLatestActivityType,
  CustomStatusResponseType,
  IContact,
  OriginInformation,
  ShipmentMode,
  THaulageDetail,
  TTPMilestone,
} from 'features/ShipmentDetails'
import { CustomerPortData, ISearchOptions, ISearchResult } from 'features/ShipmentsList'
import { IInvitedSignupDataJSON, IInviteeDataJSON, ISignupDataJSON, ISignupStatus } from 'features/Signup'
import { TTransportMode, TTransportType } from 'features/TransportPlan'
import { omit } from 'lodash-es'
import { LanguageISO } from 'utils/localisation'

import { HttpBaseApiWrapper } from './HttpBaseApiWrapper'
import { InitialDataResJSON } from './internal/InitialData'
import { ShipmentEmission } from './internal/ShipmentEmission'
import { URLPrefix } from './url'

interface APIProps {
  http: HttpBaseApiWrapper
}
export type ActivateParamsType = {
  token: string
  customerId: string
  userId: string
}

export type ActivationParamsType = {
  digest: string
  customerId: string
  userId: string
}

type BookingHistoryParams = {
  type: BookingHistoryType
}

type CustomerContactSavePayload = {
  contact: IContact
  shipmentId?: string
}

type CustomerContactSaveArgs = {
  customerId: string
  contactId?: string
  payload: CustomerContactSavePayload
}

export class API {
  http: HttpBaseApiWrapper

  signup = {
    getStatus: (): Promise<ISignupStatus> => {
      return this.http.get<ISignupStatus>('/accounts/signUp/status')
    },
    proceed: (params: Partial<ISignupDataJSON>): Promise<ISignupDataJSON> => {
      return this.http.post<ISignupDataJSON>('/accounts/signUp/proceed', params)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
    acceptInvitation: (params: IInvitedSignupDataJSON): Promise<any> => {
      // TODO type this
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      return this.http.post<any>('/accounts/signup/acceptInvite', params)
    },
    getInviteeData: (params): Promise<IInviteeDataJSON> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      return this.http.get<any>('/accounts/signup/invitee', { params })
    },
  }

  initial = {
    getInitialData: (): Promise<InitialDataResJSON> => {
      return this.http.get<InitialDataResJSON>('/initial', {
        urlPrefix: URLPrefix.Root,
      })
    },
  }

  billing = {
    getInvoices: (params: ISearchOptions): Promise<ISearchResult<Invoice>> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      return this.http.get<any>('/invoices', {
        urlPrefix: URLPrefix.Root,
        params,
      })
    },
    // Getting invoice data directly from billing core, This will deprecate shipping core getInvoices
    getCustomerInvoices: (params: ISearchOptions): Promise<ISearchResult<Invoice>> => {
      return this.http.get<ISearchResult<Invoice>>('/customer-invoices', {
        urlPrefix: URLPrefix.BillingCore,
        params,
      })
    },
    getPaymentTerms: async (params: { legal_entity_id?: string; customer_id: string }): Promise<GenericPaymentTerm> => {
      return this.http.get<GenericPaymentTerm>('/billing-preferences/customer-payment-terms', {
        urlPrefix: URLPrefix.BillingCore,
        params,
      })
    },
    createInvoiceClaim: async (body: Partial<InvoiceClaim>): Promise<InvoiceClaimResponse> => {
      return this.http.post('/invoice-claim', body, {
        urlPrefix: URLPrefix.BillingCore,
      })
    },
    getInvoiceClaims: async (params: InvoiceClaimsRequest): Promise<Record<string, InvoiceClaim>> => {
      return this.http.get<Record<string, InvoiceClaim>>('/invoice-claim', {
        urlPrefix: URLPrefix.BillingCore,
        params,
      })
    },
  }

  customStatus = {
    getCustomStatus: (shipmentId: string): Promise<CustomStatusResponseType> => {
      return this.http.get<CustomStatusResponseType>(`customs-status/${shipmentId}`, {
        urlPrefix: URLPrefix.CustomStatus,
      })
    },
    getCustomHistory: (shipmentId: string): Promise<CustomStatusHistoryType> => {
      return this.http.get<CustomStatusHistoryType>(`customs-status/history/${shipmentId}`, {
        urlPrefix: URLPrefix.CustomStatus,
      })
    },
    getCustomLatestUpdate: (shipmentId: string): Promise<CustomStatusLatestActivityType> => {
      return this.http.get<CustomStatusLatestActivityType>(`customs-status/latestUpdate/${shipmentId}`, {
        urlPrefix: URLPrefix.CustomStatus,
      })
    },
  }

  customers = {
    activate: (activateParams: ActivateParamsType) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      return this.http.get<any>(
        `/accounts/signUp/activate?token=${activateParams.token}&customerId=${activateParams.customerId}&userId=${activateParams.userId}`
      )
    },
    sendActivationEmail: (email: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      return this.http.post<any>('/accounts/signUp/sendActivationEmail', {
        email,
      })
    },
    resetPassword: ({ digest, customer_id, user_id, password, expiry }) => {
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/restrict-template-expressions -- // TODO: Replace any with a suitable type, TODO: Fix lint issue here */
      return this.http.post<any>(`/customers/${customer_id}/users/${user_id}/resetPassword`, {
        password,
        digest,
        expiry,
      })
    },
    requestResetPassword: (email) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      return this.http.post<any>('/customers/requestResetPassword', {
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      })
    },
    updateUser: (customerId, userId, data) => {
      const params = { redirectTo: `${window.location.origin}/settings` }
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/restrict-template-expressions -- // TODO: Replace any with a suitable type, TODO: Fix lint issue here */
      return this.http.put<any>(`/customers/${customerId}/users/${userId}`, data, { params })
    },
    getCustomerPreference: async (customerId: string): Promise<CustomerPreference | null> => {
      const { items } = await this.http.get<{
        count: number
        items: CustomerPreference[]
      }>(`/customer-preferences?customerId=${customerId}`)
      return items[0]
    },
    updateCustomerPreference: (customerPreference: CustomerPreference): Promise<CustomerPreference | null> => {
      return this.http.put<CustomerPreference>(`/customer-preferences/${customerPreference._id}`, {
        sustainability: customerPreference.sustainability,
      })
    },
    ports: (customerId: string, searchText?: string): Promise<CustomerPortData> => {
      return this.http.get<CustomerPortData>(`/customers/${customerId}/ports`, {
        params: { ...(searchText != null && { search: searchText }) },
      })
    },
    documents: {
      delete: (shipmentId: string, docId: string) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
        return this.http.delete<any>(`/customers/${shipmentId}/documents/${docId}`)
      },
    },
    checkVoucherAssociatedAndValid: (customerId: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      return this.http.get<any>(`/customers/${customerId}/voucherInfo`)
    },
    shipperContact: {
      save: ({ customerId, contactId, payload }: CustomerContactSaveArgs) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
        return this.http.post<any>(`/customers/${customerId}/shipperContact/${contactId || ''}`, payload)
      },
      delete: (customerId: string, shipperId: string, data) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
        return this.http.delete<any>(`/customers/${customerId}/shipperContact/${shipperId}`, data)
      },
    },
    consigneeContact: {
      save: ({ customerId, contactId, payload }: CustomerContactSaveArgs) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
        return this.http.post<any>(`/customers/${customerId}/consigneeContact/${contactId || ''}`, payload)
      },
      delete: (customerId: string, shipperId: string, data) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
        return this.http.delete<any>(`/customers/${customerId}/consigneeContact/${shipperId}`, data)
      },
    },
    activityFeed: {
      getActivities: (
        shipmentId: string,
        params: { from: number; size: number; activityTypes: string[] }
      ): Promise<IActivityFeedJSON> => {
        return this.http.get<IActivityFeedJSON>(
          `/activities/reference/${shipmentId}?from=${params.from}&size=${
            params.size
          }&activityTypes=${params.activityTypes.toString()}
          `,
          { urlPrefix: URLPrefix.Root }
        )
      },
      postMessage: (shipmentId: string, requestBody: { type: string; content: string }): Promise<void> => {
        return this.http.post<void>(`/activities/reference/${shipmentId}`, requestBody, {
          urlPrefix: URLPrefix.Root,
        })
      },
      addSubscriber: (shipmentId: string, id: string, idType: TIdType): Promise<void> => {
        return this.http.post<void>(
          `/v2/subscriptions/add/${shipmentId}`,
          { [idType]: id },
          { urlPrefix: URLPrefix.Subscriptions }
        )
      },
      removeSubscriber: (shipmentId: string, id: string, idType: TIdType): Promise<void> => {
        return this.http.post<void>(
          `/v2/subscriptions/remove/${shipmentId}`,
          { [idType]: id },
          { urlPrefix: URLPrefix.Subscriptions }
        )
      },
      getSubscriptions: (referenceIds: string[]): Promise<SubscriptionResponse> => {
        const mappedIDs = referenceIds.map((id) => `referenceIDs=${id}`)
        return this.http.get(`/v2/subscriptions?${mappedIDs.join('&')}`, {
          urlPrefix: URLPrefix.Subscriptions,
        })
      },
    },
    pickupDetails: {
      save: (customerId: string, shipmentId: string, data) => {
        return this.http.post<THaulageDetail | TTPMilestone>(
          `/customers/${customerId}/pickupDetails/${shipmentId}`,
          data
        )
      },
      delete: (customerId: string, shipmentId: string, data) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
        return this.http.delete<any>(`/customers/${customerId}/pickupDetails/${shipmentId}`, data)
      },
    },
    deliveryDetails: {
      save: (customerId: string, shipmentId: string, data) => {
        return this.http.post<THaulageDetail | TTPMilestone>(
          `/customers/${customerId}/deliveryDetails/${shipmentId}`,
          data
        )
      },
      delete: (customerId: string, shipmentId: string, data) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
        return this.http.delete<any>(`/customers/${customerId}/deliveryDetails/${shipmentId}`, data)
      },
    },
  }

  freighties = {
    get: (id: string): Promise<IFreightieJSON> => {
      return this.http.get<IFreightieJSON>(`/freighties/${id}`)
    },
  }

  cargoType = {
    get: (): Promise<CargoType[]> => {
      return this.http.get<CargoType[]>('/api/types/list?transportUnit=Container&isActive=true', {
        urlPrefix: URLPrefix.CargoTypeCore,
      })
    },
  }

  quoteSheets = {
    requestManualQuote: (props: {
      quoteSheetId: string
      customerNotes: string
      modes?: ShipmentMode[]
      pickupAddress?: string
      deliveryAddress?: string
      cargoReadyAt?: Date
      additionalRequirements?: IMQAdditionalRequirements
      buyersConsolidation?: BuyersConsolidationQuoteServicesJSON
      incoterm?: string
      bookingParty?: string
    }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      return this.http.post<any>(`/v1/quoteSheets/${props.quoteSheetId}/requestManualQuote/`, props)
    },
    get: (id: string) => {
      return this.http.get<IQuoteSheet>(`/v1/quoteSheets/${id}`)
    },
    book: (id, params) => {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- TODO: Fix lint issue here
      return this.http.post<BookQuoteSheetResponse>(`/v1/quoteSheets/${id}/book`, params)
    },
    decline: (id: string, reasons: string[], customReason?: string) =>
      this.http.put(`/v1/quoteSheets/${id}/decline`, { reasons, customReason }),
    withdraw: async (id: string, customerId: string): Promise<void> => {
      // @ts-expect-error TS(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
      return this.http.methodWithNoResponse(`/manualQuotes/${id}/withdraw?customerId=${customerId}`, null, {
        urlPrefix: URLPrefix.QuotationCore,
        method: 'PUT',
      })
    },
    getManualQuoteDocuments: async (manualQuoteId: string): Promise<ManualQuoteDocument[]> => {
      return this.http.get(`/documents/manual-quote/${manualQuoteId}/list`, { urlPrefix: URLPrefix.Documents })
    },
    downloadManualQuoteDocument: (manualQuoteId: string, documentId: string, preview?: boolean): Promise<Blob> => {
      return this.http.getPDF(
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `/documents/manual-quote/${manualQuoteId}/${documentId}/download?preview=${preview?.toString()}`,
        {
          urlPrefix: URLPrefix.Documents,
        }
      )
    },
    deleteManualQuoteDocument: (manualQuoteId: string, documentId: string): Promise<void> => {
      return this.http.delete<void>(
        `/documents/manual-quote/${manualQuoteId}/${documentId}`,
        {},
        { urlPrefix: URLPrefix.Documents, notifyOnError: true }
      )
    },
  }

  shipments = {
    getAll: () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      return this.http.get<any>('/shipments')
    },
    trackingEvents: (id?) => {
      if (id !== undefined) {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/restrict-template-expressions -- // TODO: Replace any with a suitable type, TODO: Fix lint issue here */
        return this.http.get<any>(`/shipments/${id}/trackingEvents`)
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      return this.http.get<any>('/shipments/trackingEvents')
    },
    notifications: (id, params) => {
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/restrict-template-expressions -- // TODO: Replace any with a suitable type, TODO: Fix lint issue here */
      return this.http.get<any>(`/shipments/${id}/notifications`, { params })
    },
    documents: {
      delete: (shipmentId: string, docId: string) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
        return this.http.delete<any>(`/shipments/${shipmentId}/documents/${docId}`)
      },
    },
    getShipmentToken: (shipmentId: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      return this.http.get<any>(`/websockets?shipmentId=${shipmentId}`)
    },
  }

  fx = {
    getConversionRates: async (request: ConversionRatesRequest): Promise<ConversionRatesResponse> => {
      return this.http.post(
        `request/${request.currencyExchangeRateId}`,
        { ratesRequest: request.pairs },
        { urlPrefix: URLPrefix.FxCore }
      )
    },
  }

  bookings = {
    browse: (params: BookingsParams) => {
      return this.http.get<PaginatedBookingsDTO>('/bookings', {
        urlPrefix: URLPrefix.Booking,
        params,
        serializationStrategy: getURLSearchString,
      })
    },
    getByID: (id: string) => {
      return this.http.get<BookingDTO>(`/bookings/${id}`, { urlPrefix: URLPrefix.Booking })
    },
    getHistoryByID: (id: string, params?: BookingHistoryParams) => {
      return this.http.get<GetHistoryDTO>(`/bookings/${id}/history`, {
        params,
        urlPrefix: URLPrefix.Booking,
      })
    },
    authorize: (id: string, comment: string) => {
      return this.http.post<void>(`/bookings/${id}/authorize`, { comment }, { urlPrefix: URLPrefix.Booking })
    },
    reject: (id: string, comment: string) => {
      return this.http.post<void>(`/bookings/${id}/reject`, { comment }, { urlPrefix: URLPrefix.Booking })
    },
    create: (requestParameters: CreateBookingDTO): Promise<CreateBookingResultDTO> => {
      return this.http.post<CreateBookingResultDTO>('/bookings', requestParameters, { urlPrefix: URLPrefix.Booking })
    },
    getDocuments: (bookingID: string) => {
      return this.http.get(`/documents/booking/${bookingID}`, { urlPrefix: URLPrefix.Documents, notifyOnError: true })
    },
    downloadBookingDocument: (bookingID: string, documentID: string, preview?: boolean): Promise<Blob> => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      return this.http.getPDF(`/documents/booking/${bookingID}/${documentID}/download?preview=${preview?.toString()}`, {
        urlPrefix: URLPrefix.Documents,
      })
    },
    deleteBookingDocument: (bookingID: string, documentID: string): Promise<void> => {
      return this.http.delete<void>(
        `/documents/booking/${bookingID}/${documentID}`,
        {},
        { urlPrefix: URLPrefix.Documents, notifyOnError: true }
      )
    },
    // Endpoint to check if booking creation is allowed for Yangtze program customers
    // This is a temporary solution and will be removed once booking is released to all customers
    isBookingAllowed: (payload: AllowedBookingRequestDTO) => {
      return this.http.post<AllowedBookingDTO>('/booking-allowed', payload, { urlPrefix: URLPrefix.Booking })
    },
    updateBooking: (bookingID: string, booking: PartialUpdateBookingDTO) => {
      return this.http.methodWithNoResponse<void>(`/bookings/${bookingID}`, booking, {
        urlPrefix: URLPrefix.Booking,
        method: 'PATCH',
      })
    },

    getShipperLENames: () => {
      return this.http.get<string[]>('/shipperLegalEntities/names', { urlPrefix: URLPrefix.Booking })
    },
  }

  carriers = {
    getByID: (carrierID: string): Promise<OceanCarrier> => {
      return this.http.get<OceanCarrier>(`/carriers/${carrierID}`, { urlPrefix: URLPrefix.Base, notifyOnError: true })
    },
  }

  documents = {
    getDocumentTypesList: (types?: TTransportType, modes?: TTransportMode): Promise<DocumentType[]> => {
      // hotfix: an empty array will result in an empty string when serializing.
      //         the query list?types='' means no results will be returned
      // TODO: remove once fix has been merged upstream in ui-platform repo
      const params: { types?: string; modes?: string } = {}
      if (types) {
        params.types = types
      }
      if (modes) {
        params.modes = modes
      }

      return this.http.get(`/document-types/list/`, {
        urlPrefix: URLPrefix.Documents,
        params,
      })
    },
    getDocumentsForEntity: (entityType: string, entityID: string): Promise<PaginatedDocuments> => {
      return this.http.get(`/documents/${entityType}/${entityID}`, {
        urlPrefix: URLPrefix.Documents,
      })
    },
    getCompanyDocumentTemplates: (): Promise<CompanyDocumentTemplate[]> => {
      return this.http.get(`/company-document-templates/list`, {
        urlPrefix: URLPrefix.Documents,
      })
    },
    getFilledDocument: (id: string, language: LanguageISO): Promise<BlobPart> => {
      return this.http.getPDF(`/company-document-templates/${id}`, {
        urlPrefix: URLPrefix.Documents,
        params: {
          language,
        },
      })
    },
    getDocumentPreview: (entityType: string, entityID: string, documentID: string): Promise<BlobPart> => {
      return this.http.getPDF(`/documents/${entityType}/${entityID}/${documentID}/download`, {
        urlPrefix: URLPrefix.Documents,
      })
    },
  }

  userProfileCore = {
    savedFilters: {
      getSavedFilters: async (): Promise<GetProfilesResponse> => {
        return this.http.get('/profiles/shipments-saved-filter', {
          urlPrefix: URLPrefix.UserProfileCore,
        })
      },
      createSavedFilter: async (body: CreateProfileRequest): Promise<ProfileResponse> => {
        return this.http.post('/profiles/shipments-saved-filter', body, {
          urlPrefix: URLPrefix.UserProfileCore,
        })
      },
      deleteSavedFilter: async (id: string): Promise<void> => {
        // @ts-expect-error TS(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
        return this.http.delete(`/profiles/shipments-saved-filter/${id}`, null, {
          urlPrefix: URLPrefix.UserProfileCore,
        })
      },
    },
    bookmarks: {
      getBookmarkedShipments: async (): Promise<GetProfilesResponse> => {
        return this.http.get('/profiles/shipment-bookmark', {
          urlPrefix: URLPrefix.UserProfileCore,
        })
      },
      saveBookmark: async (body: CreateProfileRequest): Promise<ProfileResponse> => {
        return this.http.post('/profiles/shipment-bookmark', body, {
          urlPrefix: URLPrefix.UserProfileCore,
        })
      },
      removeBookmark: async (id: string): Promise<void> => {
        // @ts-expect-error TS(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
        return this.http.delete(`/profiles/shipment-bookmark/${id}`, null, {
          urlPrefix: URLPrefix.UserProfileCore,
        })
      },
    },
    shipUserPreference: {
      getShipUserPreference: async (): Promise<{
        owner: {
          id: string
          value: {
            useLegacyUI: boolean
          }
        }[]
      }> => {
        return this.http.get('/profiles/ship-user-preference', {
          urlPrefix: URLPrefix.UserProfileCore,
        })
      },
      removeShipUserPreference: async (id: string): Promise<ProfileResponse> => {
        // @ts-expect-error TS(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
        return this.http.delete(`/profiles/ship-user-preference/${id}`, null, {
          urlPrefix: URLPrefix.UserProfileCore,
        })
      },
    },
    featureFlags: {
      getFeatureFlags: async (searchFeatureFlags: SearchFeatureFlagsRequest): Promise<SearchFeatureFlagsResponse> => {
        return this.http.post('/feature-flags/search', searchFeatureFlags, {
          urlPrefix: URLPrefix.UserProfileCore,
        })
      },
    },
  }
  users = {
    get: (params?: Partial<{ organizationId: string; rolesIn: string[] }>) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      return this.http.get<any>('/v1/users', { params })
    },
    add: (body) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      return this.http.post<any>(`/v1/users`, body)
    },
    delete: (userId) => {
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/restrict-template-expressions -- // TODO: Replace any with a suitable type, TODO: Fix lint issue here */
      return this.http.delete<any>(`/v1/users/${userId}`)
    },
    invite: (userId) => {
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/restrict-template-expressions -- // TODO: Replace any with a suitable type, TODO: Fix lint issue here */
      return this.http.post<any>(`/v1/users/${userId}/sendInvitation`)
    },
    getUserToken: () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      return this.http.get<any>(`/websockets`)
    },
    updateInteraction: () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      return this.http.post<any>(`/v1/users/interaction`)
    },
  }
  costBreakdown = {
    getCostBreakdown: async (shipmentId: string): Promise<CostBreakdown> => {
      return this.http.get<CostBreakdown>(`/cost-breakdown/${shipmentId}`, {
        urlPrefix: URLPrefix.BillingCore,
      })
    },
  }

  publicApi = {
    getTokensPage: (page?: number, pageSize?: number): Promise<PaginatedTokens> => {
      return this.http.get(`/tokens`, {
        urlPrefix: URLPrefix.PublicApi,
        params: { page, pageSize },
      })
    },

    createToken: (name?: string): Promise<GeneratedToken> => {
      return this.http.post(
        `tokens`,
        { name },
        {
          urlPrefix: URLPrefix.PublicApi,
        }
      )
    },

    deleteToken: (id: string): Promise<void> => {
      return this.http.delete(
        `/tokens/${id}`,
        {},
        {
          urlPrefix: URLPrefix.PublicApi,
        }
      )
    },

    listTokenLogs: (id: string, page?: number, pageSize?: number): Promise<PaginatedTokenLogs> => {
      return this.http.get(`/tokens/${id}/logs`, {
        urlPrefix: URLPrefix.PublicApi,
        params: { page, pageSize },
      })
    },

    getSubscriptionsPage: (page?: number, pageSize?: number): Promise<PaginatedSubscriptions> => {
      return this.http.get(`/events-api/subscriptions`, {
        urlPrefix: URLPrefix.PublicApi,
        params: { page, pageSize },
      })
    },

    createSubscription: (eventTypes: string[], url: string, name?: string): Promise<SubscriptionDTO> => {
      const toCreate: Pick<SubscriptionDTO, 'eventTypes' | 'url' | 'name'> = { eventTypes, url }
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      if (name?.length > 0) toCreate.name = name
      return this.http.post(`/events-api/subscriptions`, toCreate, {
        urlPrefix: URLPrefix.PublicApi,
      })
    },

    deleteSubscription: (id: string): Promise<void> => {
      return this.http.delete(
        `/events-api/subscriptions/${id}`,
        {},
        {
          urlPrefix: URLPrefix.PublicApi,
        }
      )
    },
  }

  emissions = {
    getEmissionsForShipment: (shipmentId: string): Promise<ShipmentEmission> => {
      return this.http.get(`/emissions?shipmentId=${shipmentId}`, {
        urlPrefix: URLPrefix.CarbonEmissions,
      })
    },
  }

  partnerBookingCore = {
    getOriginInformation: (status: string, shipmentId: string): Promise<OriginInformation> => {
      return this.http.get(`partner-bookings/type/${status}/status?shipmentID=${shipmentId}`, {
        urlPrefix: URLPrefix.PartnerBookingCore,
      })
    },
  }

  network = {
    contacts: {
      getContacts: (): Promise<IContactResJSON> => {
        return this.http.get<IContactResJSON>('/contacts', {
          urlPrefix: URLPrefix.Root,
        })
      },
      getContact: (_id: string): Promise<IContactJSON> => {
        return this.http.get<IContactJSON>(`/contacts/${_id}`, {
          urlPrefix: URLPrefix.Root,
        })
      },
      getManyContact: (_ids: string): Promise<IContactJSON[]> => {
        return this.http.get<IContactJSON[]>(`/contacts?ids=${_ids}`, {
          urlPrefix: URLPrefix.Root,
        })
      },
      searchContact: (searchText: string, organizationId: string): Promise<IContactResJSON> => {
        return this.http.get<IContactResJSON>(`/contacts/organizations/${organizationId}?query=${searchText}`, {
          urlPrefix: URLPrefix.Root,
        })
      },
      saveContact: (contact: Partial<IContactJSON>): Promise<IContactJSON> => {
        if (contact._id) {
          return this.http.put<IContactJSON>(`/contacts/${contact._id}`, omit(contact, '_id'), {
            urlPrefix: URLPrefix.Root,
          })
        }
        return this.http.post<IContactJSON>('/contacts', contact, {
          urlPrefix: URLPrefix.Root,
        })
      },
      deleteContact: (contactId: string) => {
        // @ts-expect-error TS(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
        return this.http.delete<void>(`/contacts/${contactId}`, null, {
          urlPrefix: URLPrefix.Root,
        })
      },
    },
    locations: {
      getLocations: (): Promise<ILocationResJSON> => {
        return this.http.get<ILocationResJSON>('/locations', {
          urlPrefix: URLPrefix.Root,
        })
      },
      getLocation: (_id: string): Promise<ILocationModelJSON> => {
        return this.http.get<ILocationModelJSON>(`/locations/${_id}`, {
          urlPrefix: URLPrefix.Root,
        })
      },
      getManyLocation: (_ids: string): Promise<ILocationModelJSON[]> => {
        return this.http.get<ILocationModelJSON[]>(`/locations?ids=${_ids}`, {
          urlPrefix: URLPrefix.Root,
        })
      },
      searchLocation: (searchText: string): Promise<ILocationResJSON> => {
        return this.http.get<ILocationResJSON>(`/locations?query=${searchText}`, { urlPrefix: URLPrefix.Root })
      },
      saveLocation: (location: Partial<ILocationModelJSON>): Promise<ILocationModelJSON> => {
        if (location._id) {
          return this.http.put<ILocationModelJSON>(`/locations/${location._id}`, omit(location, '_id'), {
            urlPrefix: URLPrefix.Root,
          })
        }
        return this.http.post<ILocationModelJSON>('/locations', location, {
          urlPrefix: URLPrefix.Root,
        })
      },
      deleteLocation: (locationId: string) => {
        // @ts-expect-error TS(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
        return this.http.delete<void>(`/locations/${locationId}`, null, {
          urlPrefix: URLPrefix.Root,
        })
      },
    },
    organization: {
      getAllOrganizations: (): Promise<IOrganizationResJSON> => {
        return this.http.get<IOrganizationResJSON>('/organizations?size=0', {
          urlPrefix: URLPrefix.Root,
        })
      },
      getOrganization: (_id: string): Promise<IOrganizationJSON> => {
        return this.http.get<IOrganizationJSON>(`/organizations/${_id}`, {
          urlPrefix: URLPrefix.Root,
        })
      },
      getManyOrganization: (_ids: string): Promise<IOrganizationJSON[]> => {
        return this.http.get<IOrganizationJSON[]>(`/organizations?ids=${_ids}`, { urlPrefix: URLPrefix.Root })
      },
      searchOrganization: (searchText: string): Promise<IOrganizationResJSON> => {
        return this.http.get<IOrganizationResJSON>(`/organizations?query=${searchText}`, {
          urlPrefix: URLPrefix.Root,
        })
      },
      addOrganization: (name: string): Promise<void> => {
        return this.http.post<void>('/organizations', { name }, { urlPrefix: URLPrefix.Root })
      },
      saveOrganization: (organization: Partial<IOrganizationJSON>): Promise<IOrganizationJSON> => {
        if (organization._id) {
          return this.http.put<IOrganizationJSON>(`/organizations/${organization._id}`, omit(organization, '_id'), {
            urlPrefix: URLPrefix.Root,
          })
        }
        return this.http.post<IOrganizationJSON>('/organizations', organization, { urlPrefix: URLPrefix.Root })
      },
      deleteOrganization: (organizationId: string) => {
        // @ts-expect-error TS(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
        return this.http.delete<void>(`/organizations/${organizationId}`, null, { urlPrefix: URLPrefix.Root })
      },
    },
    legalEntities: {
      getV3ByCode: (code: string): Promise<LegalEntityV3DTO> => {
        return this.http.get<LegalEntityV3DTO>(`/v3/legal-entities/${code}`, {
          urlPrefix: URLPrefix.CustomerMasterData,
        })
      },

      getLegalEntities: (): Promise<ILegalEntityResJSON> => {
        return this.http.get<ILegalEntityResJSON>('/legalEntities', {
          urlPrefix: URLPrefix.Root,
        })
      },
      getLegalEntity: (_id: string): Promise<ILegalEntityJSON> => {
        return this.http.get<ILegalEntityJSON>(`/legalEntities/${_id}`, {
          urlPrefix: URLPrefix.Root,
        })
      },
      getManyLegalEntity: (_ids: string): Promise<ILegalEntityResJSON> => {
        return this.http.get<ILegalEntityResJSON>(`/legalEntities?ids=${_ids}`, {
          urlPrefix: URLPrefix.Root,
        })
      },
      searchLegalEntity: (searchText: string): Promise<ILegalEntityResJSON> => {
        return this.http.get<ILegalEntityResJSON>(`/legalEntities?query=${searchText}`, {
          urlPrefix: URLPrefix.Root,
        })
      },
      saveLegalEntity: (legalEntity: Partial<ILegalEntityJSON>): Promise<ILegalEntityJSON> => {
        if (legalEntity._id) {
          return this.http.put<ILegalEntityJSON>(`/legalEntities/${legalEntity._id}`, omit(legalEntity, '_id'), {
            urlPrefix: URLPrefix.Root,
          })
        }
        return this.http.post<ILegalEntityJSON>('/legalEntities', legalEntity, {
          urlPrefix: URLPrefix.Root,
        })
      },
    },
    billingEntities: {
      getBillingEntities: (): Promise<IBillingEntityJSON[]> => {
        return this.http.get<IBillingEntityJSON[]>('/billing-entities/summary', {
          urlPrefix: URLPrefix.Root,
        })
      },
    },
  }

  vat = {
    validateVatId: (vatId: string) => {
      return this.http.get<IVATResponseJSON>(`/customer-core/vat/${vatId}`, {
        urlPrefix: URLPrefix.CustomerMasterData,
      })
    },
  }

  contacts = {
    getById: (id: string): Promise<ContactV2DTO> => {
      return this.http.get<ContactV2DTO>(`/contacts/${id}`, {
        urlPrefix: URLPrefix.CustomerMasterData,
      })
    },
  }

  constructor({ http }: APIProps) {
    this.http = http
  }

  notifications(params) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
    return this.http.get<any>('/v1/notifications', { params })
  }

  authenticate(email, password) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
    return this.http.post<any>('/authenticate', { email, password })
  }
}
